import React, { useState, useEffect } from 'react';
import { BusinessIdea } from '../../types/ai';
import { useLanguage } from '../../context/LanguageContext';
import { BusinessKitEnhancements } from './BusinessKitEnhancements';
import './TabbedIdeaSelector.css';

interface TabbedIdeaSelectorProps {
  ideas: BusinessIdea[];
  onIdeaSelected?: (idea: BusinessIdea) => void;
  onSelectionChange?: (selected: BusinessIdea[]) => void | Promise<void>;
  selectedIdeas?: BusinessIdea[];
  maxSelection?: number;
  isLoading?: boolean;
  currentStep?: number;
  totalSteps?: number;
}

interface ValidationResponse {
  response: 'yes' | 'maybe' | 'no';
  comment?: string;
}

export const TabbedIdeaSelector: React.FC<TabbedIdeaSelectorProps> = ({
  ideas,
  onIdeaSelected,
  onSelectionChange,
  selectedIdeas = [],
  maxSelection = 1,
  isLoading = false,
  currentStep = 3,
  totalSteps = 3
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState<'tabs' | 'comparison'>('tabs');
  const [expandedTechnical, setExpandedTechnical] = useState<number | null>(null);
  const [validationResponses, setValidationResponses] = useState<Record<number, ValidationResponse>>({});

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (viewMode === 'tabs') {
        if (e.key === 'ArrowLeft' && activeTab > 0) {
          setActiveTab(activeTab - 1);
        } else if (e.key === 'ArrowRight' && activeTab < ideas.length - 1) {
          setActiveTab(activeTab + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeTab, ideas.length, viewMode]);

  const handleSelectIdea = (idea: BusinessIdea) => {
    if (onIdeaSelected) {
      onIdeaSelected(idea);
    }
    if (onSelectionChange) {
      onSelectionChange([idea]);
    }
  };

  const handleValidationResponse = (ideaIndex: number, response: ValidationResponse) => {
    setValidationResponses(prev => ({
      ...prev,
      [ideaIndex]: response
    }));
  };

  const toggleTechnicalDetails = (ideaIndex: number) => {
    setExpandedTechnical(prev => prev === ideaIndex ? null : ideaIndex);
  };

  const getTechStackFromIdea = (idea: BusinessIdea): string[] => {
    // Extract likely tech stack from the solution text
    const techKeywords = ['React', 'Node.js', 'Python', 'API', 'WebRTC', 'Shopify', 'OAuth', 'JavaScript', 'TypeScript'];
    return techKeywords.filter(tech => 
      idea.solution.toLowerCase().includes(tech.toLowerCase()) ||
      idea.problem.toLowerCase().includes(tech.toLowerCase())
    );
  };

  const getInfraCost = (idea: BusinessIdea): string => {
    // Simple heuristic based on complexity
    if (idea.technicalFeasibility === 'high') return '$50-100/mo';
    if (idea.technicalFeasibility === 'medium') return '$100-250/mo';
    return '$250-500/mo';
  };

  if (ideas.length === 0) {
    return (
      <div className="idea-tabs-container">
        <div className="empty-state">
          <div className="empty-icon">💡</div>
          <p>{t('journey.ideaSelectorNoIdeas')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="idea-tabs-container">
      {/* View Mode Toggle */}
      <div className="view-controls">
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'tabs' ? 'active' : ''}`}
            onClick={() => setViewMode('tabs')}
            type="button"
          >
            <i className="bi bi-stack" />
            {t('journey.ideaSelectorIndividualView')}
          </button>
          <button
            className={`toggle-btn ${viewMode === 'comparison' ? 'active' : ''}`}
            onClick={() => setViewMode('comparison')}
            type="button"
          >
            <i className="bi bi-columns" />
            {t('journey.ideaSelectorCompareIdeas')}
          </button>
        </div>
        
        <div className="keyboard-hint">
          <i className="bi bi-keyboard" />
          {t('journey.ideaSelectorKeyboardHint')}
        </div>
      </div>

      {viewMode === 'tabs' ? (
        <>
          {/* Enhanced Tab Headers */}
          <div className="idea-tabs-header">
            {ideas.map((idea, index) => (
              <button
                key={index}
                className={`idea-tab ${activeTab === index ? 'active' : ''}`}
                onClick={() => setActiveTab(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setActiveTab(index);
                  }
                }}
                type="button"
                style={{ '--idea-color': `hsl(${index * 120}, 60%, 50%)` } as React.CSSProperties}
              >
                <div className="tab-header-content">
                  <div className="tab-icon" style={{ backgroundColor: `hsl(${index * 120}, 60%, 90%)`, color: `hsl(${index * 120}, 60%, 40%)` }}>
                    {index + 1}
                  </div>
                  <div className="tab-info">
                    <span className="tab-preview">{idea.title}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Enhanced Tab Content */}
          <div className="idea-tab-content">
            {ideas[activeTab] && (
              <div className="enhanced-idea-card">
                <h3 className="enhanced-idea-title">
                  {ideas[activeTab].title}
                  <span className={`feasibility-badge ${ideas[activeTab].technicalFeasibility.toLowerCase()}`}>
                    ({t(`journey.ideaSelectorFeasibility${ideas[activeTab].technicalFeasibility.charAt(0).toUpperCase() + ideas[activeTab].technicalFeasibility.slice(1).toLowerCase()}`)})
                  </span>
                </h3>
                
                {/* Problem & Solution with enhanced styling */}
                <div className="problem-solution-section">
                  <div className="problem-card">
                    <div className="section-icon">⚡</div>
                    <div className="section-content">
                      <h4 className="section-title">{t('journey.ideaSelectorProblem')}</h4>
                      <p className="section-text">{ideas[activeTab].problem}</p>
                    </div>
                  </div>
                  
                  <div className="solution-card">
                    <div className="section-icon">💡</div>
                    <div className="section-content">
                      <h4 className="section-title">{t('journey.ideaSelectorSolution')}</h4>
                      <p className="section-text">{ideas[activeTab].solution}</p>
                    </div>
                  </div>
                </div>

                {/* Enhanced Metadata Grid */}
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <div className="metadata-icon">👥</div>
                    <div className="metadata-content">
                      <div className="metadata-label">{t('journey.ideaSelectorTargetCustomer')}</div>
                      <div className="metadata-value">{ideas[activeTab].targetCustomer}</div>
                    </div>
                  </div>
                  
                  <div className="metadata-item">
                    <div className="metadata-icon">🛠️</div>
                    <div className="metadata-content">
                      <div className="metadata-label">{t('journey.ideaSelectorTechnicalFeasibility')}</div>
                      <div className={`metadata-value feasibility-${ideas[activeTab].technicalFeasibility.toLowerCase()}`}>
                        {t(`journey.ideaSelectorFeasibility${ideas[activeTab].technicalFeasibility.charAt(0).toUpperCase() + ideas[activeTab].technicalFeasibility.slice(1).toLowerCase()}`)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="metadata-item">
                    <div className="metadata-icon">⏱️</div>
                    <div className="metadata-content">
                      <div className="metadata-label">{t('journey.ideaSelectorDevelopmentTime')}</div>
                      <div className="metadata-value">{ideas[activeTab].estimatedDevelopment}</div>
                    </div>
                  </div>
                  
                  <div className="metadata-item">
                    <div className="metadata-icon">💰</div>
                    <div className="metadata-content">
                      <div className="metadata-label">{t('journey.ideaSelectorMonthlyInfrastructure')}</div>
                      <div className="metadata-value">{getInfraCost(ideas[activeTab])}</div>
                    </div>
                  </div>
                </div>

                {/* Collapsible Technical Details */}
                <div className="technical-details">
                  <button
                    className="technical-toggle"
                    onClick={() => toggleTechnicalDetails(activeTab)}
                    type="button"
                  >
                    <span>{t('journey.ideaSelectorTechnicalDetails')}</span>
                    <i className={`bi bi-chevron-${expandedTechnical === activeTab ? 'up' : 'down'}`} />
                  </button>
                  
                  {expandedTechnical === activeTab && (
                    <div className="technical-content">
                      <div className="tech-row">
                        <div className="tech-label">{t('journey.ideaSelectorCoreTechStack')}</div>
                        <div className="tech-tags">
                          {getTechStackFromIdea(ideas[activeTab]).map((tech, i) => (
                            <span key={i} className="tech-tag">{tech}</span>
                          ))}
                          {getTechStackFromIdea(ideas[activeTab]).length === 0 && (
                            <span className="tech-tag unknown">{t('journey.ideaSelectorNeedsScoping')}</span>
                          )}
                        </div>
                      </div>
                      <div className="tech-row">
                        <div className="tech-label">{t('journey.ideaSelectorExternalIntegrations')}</div>
                        <div className="tech-value">{t('journey.ideaSelectorExternalIntegrationsValue')}</div>
                      </div>
                      <div className="tech-row">
                        <div className="tech-label">{t('journey.ideaSelectorTechnicalRisks')}</div>
                        <div className="tech-value">{t('journey.ideaSelectorTechnicalRisksValue')}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Business Kit Enhancements */}
                <BusinessKitEnhancements idea={ideas[activeTab]} />

                {/* Enhanced CTA */}
                <div className="cta-section">
                  <button
                    className="select-idea-btn"
                    onClick={() => handleSelectIdea(ideas[activeTab])}
                    disabled={isLoading}
                    type="button"
                  >
                    {isLoading ? (
                      <>
                        <i className="bi bi-hourglass-split" />
                        {t('journey.ideaSelectorSelecting')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-rocket-takeoff" />
                        {t('journey.ideaSelectorSelectButton')}
                      </>
                    )}
                  </button>
                  
                  <div className="next-steps-preview">
                    <span>{t('journey.ideaSelectorNextSteps')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Comparison View */
        <div className="comparison-view">
          <div className="comparison-grid">
            {ideas.map((idea, index) => (
              <div key={index} className="comparison-card">
                <div className="comparison-header">
                  <div className="idea-number">{t('journey.ideaSelectorIdeaNumber', { number: index + 1 })}</div>
                  <h4 className="comparison-title">{idea.title}</h4>
                </div>
                
                <div className="comparison-content">
                  <div className="comparison-row">
                    <span className="comparison-label">{t('journey.ideaSelectorComparisonProblem')}</span>
                    <span className="comparison-value full-text">{idea.problem}</span>
                  </div>
                  
                  <div className="comparison-row">
                    <span className="comparison-label">{t('journey.ideaSelectorComparisonSolution')}</span>
                    <span className="comparison-value full-text">{idea.solution}</span>
                  </div>
                  
                  <div className="comparison-row">
                    <span className="comparison-label">{t('journey.ideaSelectorComparisonTargetCustomer')}</span>
                    <span className="comparison-value">{idea.targetCustomer}</span>
                  </div>
                  
                  <div className="comparison-row">
                    <span className="comparison-label">{t('journey.ideaSelectorComparisonFeasibility')}</span>
                    <span className={`comparison-value feasibility-${idea.technicalFeasibility.toLowerCase()}`}>
                      {t(`journey.ideaSelectorFeasibility${idea.technicalFeasibility.charAt(0).toUpperCase() + idea.technicalFeasibility.slice(1).toLowerCase()}`)}
                    </span>
                  </div>
                  
                  <div className="comparison-row">
                    <span className="comparison-label">{t('journey.ideaSelectorComparisonDevTime')}</span>
                    <span className="comparison-value">{idea.estimatedDevelopment}</span>
                  </div>
                  
                  <div className="comparison-row">
                    <span className="comparison-label">{t('journey.ideaSelectorComparisonInfrastructure')}</span>
                    <span className="comparison-value">{getInfraCost(idea)}</span>
                  </div>
                </div>
                
                <button
                  className="select-comparison-btn"
                  onClick={() => handleSelectIdea(idea)}
                  disabled={isLoading}
                  type="button"
                >
                  {t('journey.ideaSelectorComparisonSelectButton')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TabbedIdeaSelector;
